import toast from "react-hot-toast";

import { BOTTOM_RIGHT_TOASTER_ID } from "../alerts/constants";

/** At most three bottom-right toasts. A fourth dismisses the oldest the same way its close button would. */
export const MAX_BOTTOM_RIGHT_TOASTS = 3;

/** Figma stacking gap. `var(--spacing-sm)` is not defined on the app shell; the spec's fallback is 12px. */
export const BOTTOM_RIGHT_TOAST_GAP = 12;

type ToastSnapshot = { id: string; visible: boolean; dismissed: boolean };

const dismissers = new Map<string, () => void>();
/** Oldest first. */
const order: string[] = [];
/** Ids already observed in a toaster snapshot, so a stale snapshot cannot drop a toast it has never seen. */
const seen = new Set<string>();

function drop(id: string) {
  dismissers.delete(id);
  seen.delete(id);
  const index = order.indexOf(id);
  if (index !== -1) order.splice(index, 1);
}

function evictOverflow() {
  const guard = order.length + 1;
  let spins = 0;
  while (order.length > MAX_BOTTOM_RIGHT_TOASTS && spins < guard) {
    spins += 1;
    const oldestId = order[0];
    if (!oldestId) return;
    const dismiss = dismissers.get(oldestId);
    if (!dismiss) {
      drop(oldestId);
      toast.remove(oldestId, BOTTOM_RIGHT_TOASTER_ID);
      continue;
    }
    dismiss();
    if (order[0] === oldestId) drop(oldestId);
  }
}

/**
 * Remember how this toast closes. Call before `toast.custom`.
 * The returned function is the close button: eviction calls that same function.
 */
export function registerBottomRightToastDismiss(id: string, dismiss: () => void): () => void {
  const run = () => {
    if (dismissers.get(id) !== run) return;
    drop(id);
    dismiss();
  };
  dismissers.set(id, run);
  if (!order.includes(id)) {
    order.push(id);
    evictOverflow();
  }
  return run;
}

export function removeBottomRightToast(id: string) {
  drop(id);
  toast.remove(id, BOTTOM_RIGHT_TOASTER_ID);
}

export function dismissBottomRightToast(id: string) {
  drop(id);
  toast.dismiss(id, BOTTOM_RIGHT_TOASTER_ID);
}

export function bottomRightToastOptions(
  id: string,
  options?: { duration?: number; removeDelay?: number },
): {
  id: string;
  position: "bottom-right";
  toasterId: string;
  duration?: number;
  removeDelay?: number;
} {
  return {
    id,
    position: "bottom-right",
    toasterId: BOTTOM_RIGHT_TOASTER_ID,
    ...options,
  };
}

/**
 * Drop toasts the host already closed (timeout, `toast.remove` elsewhere) without repeating their dismiss.
 * Ids that have never appeared in a snapshot are left alone.
 */
export function noteBottomRightToastSnapshot(toasts: readonly ToastSnapshot[]) {
  const byId = new Map(toasts.map((item) => [item.id, item]));
  for (const id of order) {
    if (byId.has(id)) seen.add(id);
  }
  for (const id of [...order]) {
    if (!seen.has(id)) continue;
    const item = byId.get(id);
    if (!item || !item.visible || item.dismissed) drop(id);
  }
}

export function resetBottomRightToastStack() {
  dismissers.clear();
  order.length = 0;
  seen.clear();
}
